import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  App,
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Spin,
  Typography,
  Descriptions,
  Empty
} from 'antd';
import {
  ArrowLeftOutlined,
  FileExcelOutlined,
  CalendarOutlined,
  BarChartOutlined,
  RiseOutlined,
  FallOutlined,
  SwapOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import { canGenerateInventoryReports } from '../modules/inventory/inventoryAccessPolicy';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currency';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;

interface MonthlyReport {
  id: string;
  month: number;
  year: number;
  openingQuantity: number;
  openingValuePLN: number;
  receiptsQuantity: number;
  receiptsValuePLN: number;
  releasesQuantity: number;
  releasesValuePLN: number;
  adjustmentsQuantity: number;
  adjustmentsValuePLN: number;
  closingQuantity: number;
  closingValuePLN: number;
  isClosed: boolean;
  closedAt?: string;
  generatedAt: string;
}

interface ReportSummary {
  openingBalance: number;
  closingBalance: number;
  totalReceipts: number;
  totalReleases: number;
  receiptsValue: number;
  releasesValue: number;
}

interface ProductValuation {
  sku: string;
  productName: string;
  quantity: number;
  averageCost: number;
  totalValue: number;
}

const InventoryReports: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['inventory', 'common']);
  const { message } = App.useApp();

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs().subtract(1, 'month'));
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [valuation, setValuation] = useState<ProductValuation[]>([]);
  const [pzDocuments, setPzDocuments] = useState<any[]>([]);
  const [wzDocuments, setWzDocuments] = useState<any[]>([]);

  const canGenerate = canGenerateInventoryReports(authService.getCurrentUser());

  useEffect(() => {
    if (selectedMonth) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const year = selectedMonth.year();
      const month = selectedMonth.month() + 1; // dayjs months are 0-indexed

      const response = await api.get(`/reports/inventory/monthly/${year}/${month}`);

      if (response.data.report) {
        setReport(response.data.report);
        setSummary({
          openingBalance: response.data.report.openingValuePLN,
          closingBalance: response.data.report.closingValuePLN,
          totalReceipts: response.data.report.receiptsQuantity,
          totalReleases: response.data.report.releasesQuantity,
          receiptsValue: response.data.report.receiptsValuePLN,
          releasesValue: response.data.report.releasesValuePLN,
        });
      } else {
        setReport(null);
        setSummary(null);
      }

      if (response.data.valuation) {
        setValuation(response.data.valuation);
      } else {
        setValuation([]);
      }

      if (response.data.pzDocuments) {
        setPzDocuments(response.data.pzDocuments);
      } else {
        setPzDocuments([]);
      }

      if (response.data.wzDocuments) {
        setWzDocuments(response.data.wzDocuments);
      } else {
        setWzDocuments([]);
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        message.error(error.response?.data?.error || t('inventory:error.fetchFailed'));
      } else {
        setReport(null);
        setSummary(null);
        setValuation([]);
        setPzDocuments([]);
        setWzDocuments([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const year = selectedMonth.year();
      const month = selectedMonth.month() + 1;

      await api.post(`/reports/inventory/monthly/${year}/${month}/generate`);
      message.success(t('inventory:success.reportGenerated'));
      fetchReport();
    } catch (error: any) {
      message.error(error.response?.data?.error || t('inventory:error.generateReportFailed'));
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const year = selectedMonth.year();
      const month = selectedMonth.month() + 1;

      const response = await api.get(`/reports/inventory/monthly/${year}/${month}/export`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory-report-${year}-${month.toString().padStart(2, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(t('inventory:success.exported'));
    } catch (error: any) {
      message.error(error.response?.data?.error || t('inventory:error.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const valuationColumns = [
    {
      title: t('inventory:fields.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: 150,
    },
    {
      title: t('inventory:fields.productName'),
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: t('inventory:fields.quantityAvailable'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      align: 'right' as const,
    },
    {
      title: t('inventory:valuation.averageCost'),
      dataIndex: 'averageCost',
      key: 'averageCost',
      width: 140,
      align: 'right' as const,
      render: (cost: number) => formatCurrency(cost, 'PLN'),
    },
    {
      title: t('inventory:valuation.totalCost'),
      dataIndex: 'totalValue',
      key: 'totalValue',
      width: 150,
      align: 'right' as const,
      render: (value: number) => formatCurrency(value, 'PLN'),
    },
  ];

  const pzColumns = [
    {
      title: t('common:documentNumber'),
      dataIndex: 'pzNumber',
      key: 'pzNumber',
      render: (text: string, record: any) => (
        <Button type="link" onClick={() => navigate(`/app/pz/${record.id}`)}>
          {text}
        </Button>
      ),
    },
    {
      title: t('common:date'),
      dataIndex: 'receiptDate',
      key: 'receiptDate',
      render: (date: string) => formatDate(date),
    },
    {
      title: t('common:supplier'),
      dataIndex: 'supplierName',
      key: 'supplierName',
    },
    {
      title: t('inventory:fields.quantityTotal'),
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      align: 'right' as const,
    },
    {
      title: t('inventory:fields.totalValue'),
      dataIndex: 'totalValue',
      key: 'totalValue',
      align: 'right' as const,
      render: (value: number, record: any) =>
        formatCurrency(value, record.currency || 'PLN'),
    },
  ];

  const wzColumns = [
    {
      title: t('common:documentNumber'),
      dataIndex: 'wzNumber',
      key: 'wzNumber',
      render: (text: string, record: any) => (
        <Button type="link" onClick={() => navigate(`/app/wz/${record.id}`)}>
          {text}
        </Button>
      ),
    },
    {
      title: t('common:date'),
      dataIndex: 'issueDate',
      key: 'issueDate',
      render: (date: string) => formatDate(date),
    },
    {
      title: t('common:recipient'),
      dataIndex: 'recipientName',
      key: 'recipientName',
    },
    {
      title: t('inventory:fields.quantityTotal'),
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      align: 'right' as const,
    },
    {
      title: 'COGS (PLN)',
      dataIndex: 'costOfGoodsSold',
      key: 'costOfGoodsSold',
      align: 'right' as const,
      render: (value: number) => value ? formatCurrency(value, 'PLN') : '-',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/inventory')}>
            {t('common:back')}
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            <BarChartOutlined style={{ marginRight: 8 }} />
            {t('inventory:reports.title')}
          </Title>
        </Space>
        <Space>
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={(date) => date && setSelectedMonth(date)}
            disabledDate={(current) => current && current > dayjs().endOf('month')}
          />
          {canGenerate && (
            <Button
              type="primary"
              icon={<CalendarOutlined />}
              onClick={handleGenerate}
              loading={generating}
            >
              {t('inventory:reports.generate')}
            </Button>
          )}
          {report && (
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExport}
              loading={exporting}
            >
              {t('inventory:reports.download')}
            </Button>
          )}
        </Space>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : !report && !summary ? (
        <Card>
          <Empty
            description={t('inventory:reports.noData')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {canGenerate && (
              <Button type="primary" onClick={handleGenerate} loading={generating}>
                {t('inventory:reports.generate')}
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        <>
          {/* Report Header */}
          <Card style={{ marginBottom: 24 }}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label={t('inventory:reports.reportPeriod')}>
                {selectedMonth.format('MMMM YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory:reports.generatedAt')}>
                {report?.generatedAt ? formatDate(report.generatedAt) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory:valuation.method')}>
                {t('inventory:valuation.fifo')}
              </Descriptions.Item>
              <Descriptions.Item label={t('inventory:fields.status')}>
                {report?.isClosed ? (
                  <Text type="success">{t('common:closed')}</Text>
                ) : (
                  <Text type="warning">{t('common:open')}</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Summary Statistics */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('inventory:reports.openingBalance')}
                  value={summary?.openingBalance || 0}
                  precision={2}
                  suffix="PLN"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('inventory:reports.totalReceiptsValue')}
                  value={summary?.receiptsValue || 0}
                  precision={2}
                  suffix="PLN"
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<RiseOutlined />}
                />
                <Text type="secondary">
                  {summary?.totalReceipts || 0} szt.
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('inventory:reports.totalReleasesValue')}
                  value={summary?.releasesValue || 0}
                  precision={2}
                  suffix="PLN"
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<FallOutlined />}
                />
                <Text type="secondary">
                  {summary?.totalReleases || 0} szt.
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title={t('inventory:reports.closingBalance')}
                  value={summary?.closingBalance || 0}
                  precision={2}
                  suffix="PLN"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Balance Change Visualization */}
          <Card style={{ marginBottom: 24 }}>
            <Row align="middle" justify="center" gutter={16}>
              <Col>
                <Statistic
                  title={t('inventory:reports.openingBalance')}
                  value={summary?.openingBalance || 0}
                  precision={2}
                  suffix="PLN"
                />
              </Col>
              <Col>
                <Text style={{ fontSize: 24 }}>+</Text>
              </Col>
              <Col>
                <Statistic
                  title={t('inventory:reports.receipts')}
                  value={summary?.receiptsValue || 0}
                  precision={2}
                  suffix="PLN"
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col>
                <Text style={{ fontSize: 24 }}>-</Text>
              </Col>
              <Col>
                <Statistic
                  title={t('inventory:reports.releases')}
                  value={summary?.releasesValue || 0}
                  precision={2}
                  suffix="PLN"
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
              <Col>
                <Text style={{ fontSize: 24 }}>=</Text>
              </Col>
              <Col>
                <Statistic
                  title={t('inventory:reports.closingBalance')}
                  value={summary?.closingBalance || 0}
                  precision={2}
                  suffix="PLN"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
            </Row>
          </Card>

          {/* PZ Documents */}
          <Card
            title={
              <Space>
                <RiseOutlined style={{ color: '#3f8600' }} />
                {t('inventory:reports.receipts')} (PZ)
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Table
              dataSource={pzDocuments}
              columns={pzColumns}
              pagination={false}
              rowKey="id"
              size="small"
              locale={{ emptyText: t('inventory:reports.noData') }}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>{t('common:total')}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>
                      {pzDocuments.reduce((sum, doc) => sum + (doc.totalQuantity || 0), 0)}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <strong>
                      {formatCurrency(
                        pzDocuments.reduce((sum, doc) => sum + (doc.totalValue || 0), 0),
                        'PLN'
                      )}
                    </strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>

          {/* WZ Documents */}
          <Card
            title={
              <Space>
                <FallOutlined style={{ color: '#cf1322' }} />
                {t('inventory:reports.releases')} (WZ)
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Table
              dataSource={wzDocuments}
              columns={wzColumns}
              pagination={false}
              rowKey="id"
              size="small"
              locale={{ emptyText: t('inventory:reports.noData') }}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>{t('common:total')}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>
                      {wzDocuments.reduce((sum, doc) => sum + (doc.totalQuantity || 0), 0)}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <strong>
                      {formatCurrency(
                        wzDocuments.reduce((sum, doc) => sum + (doc.costOfGoodsSold || 0), 0),
                        'PLN'
                      )}
                    </strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>

          {/* Inventory Valuation */}
          <Card
            title={
              <Space>
                <SwapOutlined />
                {t('inventory:valuation.title')}
              </Space>
            }
          >
            <Table
              dataSource={valuation}
              columns={valuationColumns}
              pagination={{
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50', '100'],
              }}
              rowKey="sku"
              size="small"
              summary={(pageData) => {
                const totalQty = pageData.reduce((sum, item) => sum + item.quantity, 0);
                const totalVal = pageData.reduce((sum, item) => sum + item.totalValue, 0);
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <strong>{t('common:total')}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <strong>{totalQty}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                    <Table.Summary.Cell index={3} align="right">
                      <strong>{formatCurrency(totalVal, 'PLN')}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default InventoryReports;
